// Client data layer for the unified permissions admin page.
//
// - subscribePermissionUsers: live list of all users with their raw `restricted`
//   map, so the matrix can show each user's current per-service level via
//   permLevel(). Operators can read /users under the security rules; the page
//   itself self-gates to operator super_admin.
// - setUserPermission: thin wrapper over the callable of the same name.

import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, onSnapshot } from 'firebase/firestore';

import { app, firestore } from '../firebase';

export const FN_SET_USER_PERMISSION = 'setUserPermission';

export interface PermissionUser {
  uid: string;
  email: string;
  // Raw restricted map straight off the doc — read levels with
  // permLevel(user.restricted, service) from ../permissions/registry.
  restricted: Record<string, unknown>;
}

/** Live subscription to all users (sorted by email). Returns an unsubscribe. */
export function subscribePermissionUsers(
  cb: (users: PermissionUser[]) => void,
): () => void {
  return onSnapshot(collection(firestore, 'users'), (snap) => {
    const users: PermissionUser[] = [];
    snap.forEach((d) => {
      const u = d.data() as { private?: { email?: string }; restricted?: Record<string, unknown> };
      if (!u?.private?.email) return; // skip docs without an email (unusable)
      users.push({ uid: d.id, email: u.private.email, restricted: u.restricted || {} });
    });
    users.sort((a, b) => a.email.localeCompare(b.email));
    cb(users);
  });
}

/** Grant/revoke a service level for a user (level=null revokes). Super-admin only. */
export async function setUserPermission(
  email: string,
  service: string,
  level: string | null,
  scope?: Record<string, unknown>,
): Promise<{ uid: string; email: string; service: string; level: string | null }> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, FN_SET_USER_PERMISSION);
  const res = await callable({ email, service, level, scope });
  return res.data as { uid: string; email: string; service: string; level: string | null };
}
